import { Button } from "~/atoms/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/atoms/dialog";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Check } from "lucide-react";
import {
  FetchError,
  type WorkflowStep,
  type ScheduleReviewPeriod,
  type DocumentDetailsRelease,
} from "~/api/types";
import { downloadArtifact, updateDocumentRelease } from "~/api/v2/requests";
import {
  analyzeTrackChanges,
  type TrackChangeStatus,
} from "~/helpers/docx-track-changes";
import { Alert, AlertDescription, AlertTitle } from "~/atoms/alert";
import { getApprovalStatus } from "~/helpers/documentPermissions";
import { Label } from "~/atoms/label";
import DatePicker from "~/molecules/DatePicker";
import {
  addDays,
  addMonths,
  isPastDate,
  toISODateString,
} from "~/helpers/date";
import { toast } from "react-toastify";
import { Spinner } from "~/atoms/spinner";
import { reviewPeriodInMonths } from "~/helpers/document";

type PublishModalProps = {
  onConfirm: (releaseId: number) => void;
  canManage: boolean;
  selectedRelease: DocumentDetailsRelease;
  lastStep: WorkflowStep | undefined;
  scheduleReviewPeriod: ScheduleReviewPeriod;
};

function computeInitialReviewDates(
  scheduleReviewPeriod: ScheduleReviewPeriod,
  selectedRelease: DocumentDetailsRelease,
): { from: Date | undefined; to: Date | undefined } {
  if (!scheduleReviewPeriod) {
    return { from: undefined, to: undefined };
  }

  const months = reviewPeriodInMonths[scheduleReviewPeriod];
  const { reviewFrom, reviewTo } = selectedRelease.artifact;

  const nextFrom = reviewFrom ? addMonths(reviewFrom, months) : undefined;
  const nextTo = reviewTo ? addMonths(reviewTo, months) : undefined;

  const resolvedFrom = nextFrom && !isPastDate(nextFrom) ? nextFrom : undefined;
  const resolvedTo = nextTo && !isPastDate(nextTo) ? nextTo : undefined;

  return {
    from: resolvedFrom,
    to:
      resolvedFrom && resolvedTo && resolvedFrom > resolvedTo
        ? undefined
        : resolvedTo,
  };
}

export function PublishModal({
  onConfirm,
  selectedRelease,
  canManage,
  lastStep,
  scheduleReviewPeriod,
}: PublishModalProps) {
  const { t } = useTranslation();
  const [trackChangeStatus, setTrackChangeStatus] =
    useState<TrackChangeStatus | null>(null);
  const [nextReviewFrom, setNextReviewFrom] = useState<Date>();
  const [nextReviewTo, setNextReviewTo] = useState<Date>();
  const showMissingReviewDatesWarning = !nextReviewFrom || !nextReviewTo;

  const [publishError, setPublishError] = useState<FetchError | null>(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [open, setOpen] = useState(false);

  const handlePublishDocument = async () => {
    setIsPublishing(true);
    setPublishError(null);

    try {
      await updateDocumentRelease(selectedRelease.id, {
        status: "published",
        nextReviewFrom: toISODateString(nextReviewFrom),
        nextReviewTo: toISODateString(nextReviewTo),
      });

      toast.success(t(($) => $.commons.workflow.documentPublishedSuccessfully));
      setOpen(false);
      onConfirm(selectedRelease.id);
    } catch (error) {
      if (error instanceof FetchError) {
        setPublishError(error);
      }
      if (
        !(error instanceof FetchError) ||
        error.code !== "DOCUMENT_OPEN_IN_EDITOR"
      ) {
        toast.error(t(($) => $.commons.workflow.failedToPublishDocument));
      }
    } finally {
      setIsPublishing(false);
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;

    const fetchTrackChangesStatus = async () => {
      if (selectedRelease.artifact.fileType !== "docx") {
        return;
      }

      const blob = await downloadArtifact(selectedRelease.artifact.id).catch(
        () => null,
      );
      if (!blob) {
        return;
      }

      const status = await analyzeTrackChanges(blob).catch(() => null);
      if (!status || cancelled) {
        return;
      }

      setTrackChangeStatus(status);
    };

    void fetchTrackChangesStatus();

    return () => {
      cancelled = true;
      setTrackChangeStatus(null);
    };
  }, [open, selectedRelease]);

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    if (open) {
      const { from, to } = computeInitialReviewDates(
        scheduleReviewPeriod,
        selectedRelease,
      );
      setNextReviewFrom(from);
      setNextReviewTo(to);
    } else {
      setPublishError(null);
    }
  };

  const { isWaitingForApprovals, approvedCount, totalCount } = lastStep
    ? getApprovalStatus(lastStep)
    : { isWaitingForApprovals: false, approvedCount: 0, totalCount: 0 };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button className="min-w-48" disabled={isWaitingForApprovals}>
          <Check />
          {isWaitingForApprovals
            ? t(($) => $.commons.workflow.waitingForApprovals, {
                approvedCount,
                totalCount,
              })
            : t(($) => $.commons.workflow.publishDocument)}
        </Button>
      </DialogTrigger>
      <DialogContent className="gap-0 p-0">
        <DialogHeader className="p-6">
          <DialogTitle>
            {t(($) => $.commons.workflow.publishDocument)}
          </DialogTitle>
          <DialogDescription>
            {canManage
              ? t(($) => $.commons.workflow.publishDocumentCanManageDescription)
              : t(
                  ($) =>
                    $.commons.workflow.publishDocumentCannotManageDescription,
                )}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 px-4 pb-4">
          <div className="flex flex-col gap-6 px-4 pb-2">
            {publishError?.code === "DOCUMENT_OPEN_IN_EDITOR" && (
              <Alert variant="destructive">
                <AlertTriangle />
                <AlertTitle>
                  {t(($) => $.commons.workflow.documentOpenInEditorError)}
                </AlertTitle>
                <AlertDescription>
                  {t(
                    ($) =>
                      $.commons.workflow.documentOpenInEditorErrorDescription,
                  )}
                </AlertDescription>
              </Alert>
            )}

            {trackChangeStatus?.hasTrackRevisionsSetting && (
              <Alert variant="warn">
                <AlertTriangle />
                <AlertTitle>
                  {t(($) => $.commons.workflow.trackChangesEnabledWarning)}
                </AlertTitle>
                <AlertDescription>
                  {t(
                    ($) =>
                      $.commons.workflow.trackChangesEnabledWarningDescription,
                  )}
                </AlertDescription>
              </Alert>
            )}

            {trackChangeStatus?.hasRevisionNodes && (
              <Alert variant="warn">
                <AlertTriangle />
                <AlertTitle>
                  {t(($) => $.commons.workflow.unresolvedSuggestionsWarning)}
                </AlertTitle>
                <AlertDescription>
                  {t(
                    ($) =>
                      $.commons.workflow
                        .unresolvedSuggestionsWarningDescription,
                  )}
                </AlertDescription>
              </Alert>
            )}

            {trackChangeStatus?.hasCommentNodes && (
              <Alert variant="warn">
                <AlertTriangle />
                <AlertTitle>
                  {t(($) => $.commons.workflow.commentsWarning)}
                </AlertTitle>
                <AlertDescription>
                  {t(($) => $.commons.workflow.commentsWarningDescription)}
                </AlertDescription>
              </Alert>
            )}

            {showMissingReviewDatesWarning && (
              <Alert variant="warn">
                <AlertTriangle />
                <AlertTitle>
                  {t(($) => $.commons.workflow.missingReviewDates)}
                </AlertTitle>
                <AlertDescription>
                  {canManage
                    ? t(
                        ($) =>
                          $.commons.workflow
                            .missingReviewDatesCanManageDescription,
                      )
                    : t(
                        ($) =>
                          $.commons.workflow
                            .missingReviewDatesCannotManageDescription,
                      )}
                </AlertDescription>
              </Alert>
            )}

            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-1">
                <Label className="text-muted-foreground">
                  {t(($) => $.commons.workflow.startDate)}
                </Label>
                <DatePicker
                  value={nextReviewFrom}
                  onChange={(date) => {
                    setNextReviewFrom(date);
                    if (!date || (nextReviewTo && date >= nextReviewTo)) {
                      setNextReviewTo(undefined);
                    }
                  }}
                  minDate={addDays(new Date(), 1)}
                  disabled={!canManage}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-muted-foreground">
                  {t(($) => $.commons.workflow.endDate)}
                </Label>
                <DatePicker
                  value={nextReviewTo}
                  onChange={setNextReviewTo}
                  minDate={
                    nextReviewFrom
                      ? addDays(nextReviewFrom, 1)
                      : addDays(new Date(), 2)
                  }
                  disabled={!nextReviewFrom || !canManage}
                />
              </div>
            </div>
          </div>

          <DialogFooter className="flex flex-row justify-end gap-2 px-6">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isPublishing}
            >
              {t(($) => $.commons.workflow.cancel)}
            </Button>
            <Button onClick={handlePublishDocument} disabled={isPublishing}>
              {isPublishing ? (
                <Spinner className="text-primary-foreground size-5"></Spinner>
              ) : (
                t(($) => $.commons.workflow.publishDocument)
              )}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
